import type { BatchPricesResponse } from "../services/pricingService";

export interface ConfigBundleViewModel {
	viewModel?: ConfigBundleData;
	staticFilePrefix: string;
	enableGenesysCallback: boolean;
	isGamesPerformanceEnabled: boolean;
	prettyUrl: string;
	pageTitle: string;
	strings: StringSystem;
	preResolvedSelections?: PreResolvedSelectionsResult;
	notFoundMessage?: string;
	isB2B: boolean;
}

export interface PreResolvedSelectionsResult {
	selections?: BatchPricesResponse;
	errorMessage?: string;
}

export interface ConfigBundleData {
	id: string;
	name: string;
	price: Price;
	productGroups: ProductGroupDto[];
	breadCrumbs: BreadCrumb[];
    tag?: Tag;
}

export interface Tag {
    title: string;
	color: string;
	url: string;
	description: string;
}

export interface Price {
	listPrice: number;
	customerPrice: number;
	discount: number;
	subscriptionPrice: number;
	taxRate: number;
	listPriceNet: number;
	customerPriceNet: number;
	discountNet: number;
	isClubPrice: boolean;
}

export interface ProductGroupDto {
	id: string;
	name: string;
	label?: string;
	requiredSelection: boolean;
	forcedDefault: boolean;
	selectionType: string;
	performanceComponentType?: string | null;
	itemType?: string | null;
	product: Product;
	/** Populated for selectionType === "Multiple" — the default pre-selected products. */
	products?: Product[];
}

export const SELECTION_TYPES = {
	SINGLE: "Single",
	ALWAYS_INCLUDED: "AlwaysIncluded",
	MULTIPLE: "Multiple",
} as const;

export interface Product {
	id: string;
	name: string;
	description: string;
	description2: string;
	productInfo: Record<string, string>;
	imageUrl: string;
	availabilityQuantity: number;
	availabilityStatus: string;
	calculatedStockQuantity: number;
	stockStatus?: {
		iconClass?: string;
		description?: string;
	};
}

export interface BreadCrumb {
	title: string;
	link: string;
}

export interface StringSystem {
	product: ProductString;
	productPage: ProductPageString;
}

export interface ProductString {
	configBundles: ConfigBundlesString;
}

export interface ProductPageString {
	section: ProductPageSectionString;
	buyBox: ProductPageBuyboxString;
}

export interface ConfigBundlesString {
	priceFrom: string;
	priceSaved: string;
	buyButton: string;
	edit: string;
	readMore: string;
	add: string;
	compare: string;
	saveToWishlist: string;
	subscriptionBuyButton: string;
	stockStatus: StockStatusString;
	subscriptionInfoButton: string;
	defaultSelectedForcedDefault: string;
	seeMore: string;
	notSelected: NotSelectedString;
	youAreHere: string;
	home: string;
	configure: string;
	partNumber: string;
	description: string;
	specifications: string;
	productInfo: string;
	loadingProductDetails: string;
	included: string;
	select: string;
	selected: string;
	cannotLoadProductDetails: string;
	share: string;
	shareConfiguration: string;
	shareSuccess: string;
	linkCopied: string;
	linkCopyFailed: string;
	productsUnavailableWarning: string;
	componentReplaced: string;
	invalidConfigurationLink: string;
	compatibility: CompatibilityString;
	reset: string;
	configured: string;
	gamesWidget: GamesWidgetString;
	shortAbout: string;
	help: HelpString;
	accessories: AccessoriesString;
	performanceBar: PerformanceBarString;
	exclVatLabel: string;
	clearSelection: string;
	chooseProduct: string;
}

export interface PerformanceBarString {
	performance: string;
	better: string;
	worse: string;
}

export interface AccessoriesString {
	pcAccessories: string;
	defaultGroupName: string;
	addedToCart: string;
	[key: string]: string;
}

export interface HelpString {
	title: string;
	description: string;
	success: string;
	phonePlaceholder: string;
	phoneError: string;
	namePlaceholder: string;
	formError: string;
	error: string;
	submitButton: string;
}

export interface GamesWidgetString {
	headerTitle: string;
	headerDescription: string;
	recommendedRequirementsMetHeader: string;
	recommendedRequirementsMet: string;
	minimumRequirementsMetHeader: string;
	minimumRequirementsMet: string;
	requirementsNotMetHeader: string;
	requirementsNotMet: string;
	notKnownHeader: string;
	notKnown: string;
	moreGames: string;
	popupMoreGames: PopupMoreGamesString;
}

export interface PopupMoreGamesString {
	headerTitle: string;
	searchForGamesPlaceholder: string;
	okButton: string;
	cancelButton: string;
	popularGamesHeader: string;
}

export interface CompatibilityString {
	compatible: string;
	notCompatible: string;
	configurationCompatible: string;
	validationError?: string;
}

export interface NotSelectedString {
	notChosen: string;
	webText1: string;
	webText2: string;
}

export interface StockStatusString {
	stocked: string;
	notInStock: string;
}

export interface ProductPageSectionString {
	// Add properties as needed from the translation system
}

export interface ProductPageBuyboxString {
	// Add properties as needed from the translation system
}

export interface BundleRecommendationResponse {
	placements: RecommendationPlacement[];
	promos: RecommendationPromo[];
}

export interface RecommendationPlacement {
	message: string;
	placement_name: string;
	items: RecommendationItem[];
}

export interface RecommendationItem {
	id: string;
	name: string;
	linkURL: string;
	imageURL: string;
	price: number;
	priceBefore: number;
	priceFormatted: string;
	title: string;
	priceBeforeFormatted: string;
	rating: number;
	showNetPrices: boolean;
	buyType: string;
	description: string;
	energyLogoModel?: EnergyLogoModel;
	imageAltText: string;
	reviewCount: number;
	currencyCode: string;
	sellerId: string;
	variantsGroup?: VariantsGroup;
	hideDiscountPromotion: boolean;
	showDiscountPromotionAsCurrency: boolean;
	productUrl: string;
	monthlyPrice?: number;
	monthlyPriceFormatted?: string;
	monthlyPriceBefore?: number;
	monthlyPriceBeforeFormatted?: string;
	totalSubscriptionPrice?: number;
	totalSubscriptionPriceFormatted?: string;
	renewalPeriod?: number;
	bundleGroup?: string;
}

export interface EnergyLogoModel {
	manufacturerName: string;
	manufacturerPartNumber: string;
	energyDetailsUrl: string;
	energyTechnicalUrl: string;
	energyGrade: number;
	energyRate: number;
	isEnergyLabelV2021: boolean;
}

export interface VariantsGroup {
	variantGroupId: string;
	productVariants: ProductVariant[];
}

export interface ProductVariant {
	products: VariantProduct[];
	unFilteredProducts: VariantProduct[];
	filteredProducts: VariantProduct[];
	type: string;
	name: string;
	groupByInListing: boolean;
}

export interface VariantProduct {
	matnr: string;
	value: string;
	displayValue: string;
	url: string;
}

export interface RecommendationPromo {
	placementName: string;
	destinationUrl: string;
	trackingUrl: string;
	altTxt: string;
	mediaUrlXs: string;
	mediaUrl: string;
	clickUrl: string;
	clickTrackingUrl: string;
	contentUrl: string;
	campaign: string;
}